import { GameSettingsSchema, type GameSettings } from '@mcpanel/core';

export function GameSettingsFields({
  value,
  onChange,
  disabled = false,
}: {
  value: GameSettings;
  onChange: (value: GameSettings) => void;
  disabled?: boolean;
}) {
  const parsed = GameSettingsSchema.safeParse(value);
  const fieldError = (key: string) =>
    parsed.success ? undefined : parsed.error.issues.find((issue) => issue.path[0] === key)?.message;
  return (
    <fieldset disabled={disabled} className="stack">
      <legend>ゲーム設定</legend>
      {(
        [
          [
            'gamemode',
            'デフォルトのゲームモード',
            [
              ['survival', 'サバイバル'],
              ['creative', 'クリエイティブ'],
              ['adventure', 'アドベンチャー'],
              ['spectator', 'スペクテイター'],
            ],
          ],
          [
            'difficulty',
            '難易度',
            [
              ['peaceful', 'ピースフル'],
              ['easy', 'イージー'],
              ['normal', 'ノーマル'],
              ['hard', 'ハード'],
            ],
          ],
        ] as const
      ).map(([key, label, choices]) => (
        <div className="field" key={key} data-search-id={`game-${key}`}>
          <label htmlFor={`game-${key}`}>{label}</label>
          <select
            id={`game-${key}`}
            className="input"
            value={value[key]}
            onChange={(e) => onChange({ ...value, [key]: e.target.value })}
          >
            {choices.map(([id, label]) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
        </div>
      ))}
      {(
        [
          ['hardcore', 'ハードコア'],
          ['pvp', 'PvP'],
          ['allow-flight', '飛行を許可'],
          ['allow-nether', 'ネザーを使う'],
        ] as const
      ).map(([key, label]) => (
        <div key={key} data-search-id={`game-${key}`}>
          <label className="check">
            <input
              type="checkbox"
              checked={Boolean(value[key])}
              ref={(input) => {
                if (input) input.indeterminate = value[key] === null;
              }}
              onChange={(e) => onChange({ ...value, [key]: e.target.checked })}
            />
            {label}
          </label>
          {value.settingMessages?.[key] && <p className="small muted">{value.settingMessages[key]}</p>}
        </div>
      ))}
      {(
        [
          ['max-players', '最大プレイヤー数', 1, 10000],
          ['spawn-protection', 'スポーン保護範囲', 0, 29999984],
        ] as const
      ).map(([key, label, min, max]) => (
        <div className="field" key={key} data-search-id={`game-${key}`}>
          <label htmlFor={`game-${key}`}>{label}</label>
          <input
            id={`game-${key}`}
            className="input"
            type="number"
            min={min}
            max={max}
            value={value[key]}
            onChange={(e) => onChange({ ...value, [key]: Number(e.target.value) })}
            aria-invalid={Boolean(fieldError(key))}
            aria-describedby={fieldError(key) ? `game-${key}-error` : undefined}
          />
          <span className="small muted">{key === 'max-players' ? '人' : 'ブロック'}</span>
          {fieldError(key) && (
            <div id={`game-${key}-error`} role="alert" style={{ color: 'var(--danger)' }}>
              {fieldError(key)}
            </div>
          )}
        </div>
      ))}
      <div className="field">
        <label data-search-id="game-motd" htmlFor="game-motd">
          サーバーの説明（MOTD）
        </label>
        <input
          id="game-motd"
          className="input"
          maxLength={200}
          value={value.motd}
          onChange={(e) => onChange({ ...value, motd: e.target.value })}
        />
      </div>
    </fieldset>
  );
}
